/**
 * Heat loss calculator ported from:
 * "Heat losses calculator PL - 05_11_2021.xlsx" sheet: Input
 *
 * Data is provided via `data.tables` (from "source data EN (master text)" or PL).
 */
export function calcRoomHeatLoss(data) {
  const inp = data.inputs
  const t = data.tables

  const outdoorTemp = num(inp.outdoorTemp_C)
  const storeyKey = num(inp.storeyKey)
  const windInfluence = num(inp.windInfluence)

  const uOuterWall = num(inp.uOuterWall_W_m2K)
  const floorColIndex = num(inp.floorColIndex)
  const ceilingColIndex = num(inp.ceilingColIndex)

  const roomTemp = num(inp.roomTemp_C)
  const ventilationChange = num(inp.ventilationChange)

  const glazingPct = num(inp.glazingPct)
  const uGlazing = num(inp.uGlazing_W_m2K)

  // ---- Dimensions ----
  const length = num(inp.length_m)
  const width = num(inp.width_m)
  const height = num(inp.height_m)
  const nWalls = num(inp.numberOfExternalWalls)

  // ---- Derived surfaces (Input!Q3..Q8) ----
  const surface = length * width // Q3
  const outerWallSurface = calcOuterWallSurface(nWalls, length, width) // Q7
  const innerWallSurface = (length + width) * 2 - outerWallSurface // Q4
  const windowSurface = outerWallSurface * glazingPct // Q8

  // ---- Roof/Ceiling table lookups (Input!Q10..Q13) ----
  const uCeiling = lookupByKey(storeyKey, t.roofCeilingData, ceilingColIndex) // Q10
  const uFloor = lookupByKey(storeyKey, t.roofCeilingData, floorColIndex) // Q11

  const underlayTemp = lookupByKey(storeyKey, t.roofCeilingData, 10) // Q12
  const aboveTempRaw = lookupByKey(storeyKey, t.roofCeilingData, 11) // Q13
  const aboveTemp = resolveAboveTemp(aboveTempRaw, outdoorTemp)

  // ---- Temperature delta (Input!Q9) ----
  const delta = roomTemp - outdoorTemp

  // ---- Heat loss components (Input!E27..E33) ----
  const outerWall_W = excelRound(
    delta * outerWallSurface * height * uOuterWall * (1 + windInfluence),
    0
  )
  const innerWall_W = excelRound((roomTemp - 20) * innerWallSurface * height, 0)
  const windows_W = excelRound(delta * windowSurface * uGlazing, 0)
  const floor_W = excelRound((roomTemp - underlayTemp) * surface * uFloor, 0)
  const ceiling_W = excelRound((roomTemp - aboveTemp) * surface * uCeiling, 0)
  const ventilation_W = excelRound(delta * surface * height * 0.34 * ventilationChange, 0)

  const heatup_W = excelRound(
    0.15 * (outerWall_W + innerWall_W + windows_W + floor_W + ceiling_W + ventilation_W),
    0
  )

  const total_W =
    outerWall_W + innerWall_W + windows_W + floor_W + ceiling_W + ventilation_W + heatup_W

  // Input!E37 / E38
  const per_m2_W = surface !== 0 ? total_W / surface : 0
  const per_m3_W = surface * height !== 0 ? total_W / (surface * height) : 0

  return {
    inputsResolved: {
      outdoorTemp_C: outdoorTemp,
      storeyKey,
      windInfluence,
      uOuterWall_W_m2K: uOuterWall,
      floorColIndex,
      ceilingColIndex,
      roomTemp_C: roomTemp,
      ventilationChange,
      glazingPct,
      uGlazing_W_m2K: uGlazing,
    },
    geometry: {
      length_m: length,
      width_m: width,
      height_m: height,
      surface_m2: surface,
      outerWallSurface_m2: outerWallSurface,
      innerWallSurface_m2: innerWallSurface,
      windowSurface_m2: windowSurface,
    },
    intermediate: {
      delta_C: delta,
      uFloor_W_m2K: uFloor,
      uCeiling_W_m2K: uCeiling,
      underlayTemp_C: underlayTemp,
      aboveTemp_C: aboveTemp,
    },
    losses_W: {
      outerWall_W,
      innerWall_W,
      windows_W,
      floor_W,
      ceiling_W,
      ventilation_W,
      heatup_W,
      total_W,
    },
    totals: {
      total_W,
      total_kW: total_W / 1000,
      per_m2_W,
      per_m3_W,
    },
  }
}

// ---------------- helpers ----------------

function calcOuterWallSurface(numberOfExternalWalls, length, width) {
  // Mirrors Input!Q7:
  // IF(D13=0,0,IF(D13=1,length,IF(D13=2,length+width,length*2+width)))
  if (numberOfExternalWalls === 0) return 0
  if (numberOfExternalWalls === 1) return length
  if (numberOfExternalWalls === 2) return length + width
  return length * 2 + width // Excel "else" branch
}

function resolveAboveTemp(aboveTempRaw, outdoorTemp) {
  // In your table, some cells are formulas like "=outdoor_temp" or "=T10"
  // which ultimately reference outdoor_temp.
  if (typeof aboveTempRaw === 'string' && aboveTempRaw.trim().startsWith('=')) {
    return outdoorTemp
  }
  return num(aboveTempRaw)
}

function lookupByKey(key, table, colIndex1Based) {
  const row = (table || []).find((r) => num(r?.[0]) === key)
  if (!row) {
    throw new Error(`Lookup: key not found: "${key}"`)
  }
  const idx = colIndex1Based - 1
  return num(row[idx])
}

function num(x) {
  if (x == null) return 0
  if (typeof x === 'number') return Number.isFinite(x) ? x : 0
  if (typeof x === 'string') {
    // tolerate decimal comma
    const v = Number(x.replace(',', '.'))
    return Number.isFinite(v) ? v : 0
  }
  return 0
}

function excelRound(value, digits) {
  // Excel ROUND rounds halves AWAY from zero.
  const factor = Math.pow(10, digits)
  const x = value * factor
  const rounded = x >= 0 ? Math.round(x) : -Math.round(-x)
  return rounded / factor
}

const exampleData = {
  inputs: {
    outdoorTemp_C: -20,
    storeyKey: 3,
    windInfluence: 0.01,
    uOuterWall_W_m2K: 0.2,
    floorColIndex: 8,
    ceilingColIndex: 9,
    roomTemp_C: 20,
    ventilationChange: 0.5,
    glazingPct: 0.4,
    uGlazing_W_m2K: 1.1,
    numberOfExternalWalls: 2,
    length_m: 3,
    width_m: 4,
    height_m: 2.5,
  },
  tables: {
    roofCeilingData: [
      [1, 1.5, 1.5, 0.6, 0.6, 0.3, 0.6, 0.2, 0.3, 5, 20],
      [2, 1.5, 1.5, 0.6, 0.6, 0.3, 0.3, 0.2, 0.2, 5, '=outdoor_temp'],
      [3, 1.5, 1.5, 1, 0.6, 0.6, 0.6, 0.3, 0.3, 20, 20],
      [4, 1.5, 1.5, 1, 0.6, 0.6, 0.3, 0.3, 0.2, 20, '=outdoor_temp'],
    ],
  },
}
